// src/utils/logger.ts
var LogLevel = /* @__PURE__ */ ((LogLevel2) => {
  LogLevel2["DEBUG"] = "debug";
  LogLevel2["INFO"] = "info";
  LogLevel2["WARN"] = "warn";
  LogLevel2["ERROR"] = "error";
  LogLevel2["FATAL"] = "fatal";
  return LogLevel2;
})(LogLevel || {});
var LogRetentionPolicy = /* @__PURE__ */ ((LogRetentionPolicy2) => {
  LogRetentionPolicy2["PERMANENT"] = "permanent";
  LogRetentionPolicy2["LONG_TERM"] = "long_term";
  LogRetentionPolicy2["SHORT_TERM"] = "short_term";
  LogRetentionPolicy2["EPHEMERAL"] = "ephemeral";
  return LogRetentionPolicy2;
})(LogRetentionPolicy || {});
var PERMANENT_CONTEXTS = [
  "AuditLogService",
  "SecurityService",
  "EncryptionService",
  "ConsentService",
  "SecureDataService"
];
var PERMANENT_MESSAGE_PATTERNS = [
  /security/i,
  /breach/i,
  /unauthorized/i,
  /consent/i,
  /GDPR/i,
  /encrypt/i,
  /decrypt/i,
  /audit/i
];
var PII_FIELDS = /* @__PURE__ */ new Set([
  "patientId",
  "patientName",
  "patientEmail",
  "email",
  "phone",
  "therapistName",
  "name",
  "fullName",
  "requesterEmail",
  "newEmail",
  "inviteEmail"
]);
var ID_FIELDS = /* @__PURE__ */ new Set(["userId", "therapistId", "relationshipId"]);
function piiHash(input) {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36).slice(0, 6);
}
function sanitizeMetadata(obj) {
  if (obj === null || obj === void 0) return obj;
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeMetadata);
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === void 0) {
      result[key] = value;
      continue;
    }
    if (typeof value === "object" && !(value instanceof Error)) {
      result[key] = sanitizeMetadata(value);
    } else if (PII_FIELDS.has(key)) {
      result[key] = `[REDACTED:${piiHash(String(value))}]`;
    } else if (ID_FIELDS.has(key) && typeof value === "string" && value.length > 4) {
      result[key] = `${value.slice(0, 4)}...${piiHash(value)}`;
    } else {
      result[key] = value;
    }
  }
  return result;
}
var Logger = class _Logger {
  // Mantener últimos 1000 logs en memoria
  constructor(context) {
    this.context = context;
  }
  static instance;
  logs = [];
  maxLogs = 1e3;
  static getLogger(context) {
    return new _Logger(context);
  }
  debug(message, metadata) {
    this.log("debug" /* DEBUG */, message, metadata);
  }
  info(message, metadata) {
    this.log("info" /* INFO */, message, metadata);
  }
  warn(message, metadata) {
    this.log("warn" /* WARN */, message, metadata);
  }
  error(message, error, metadata) {
    const errorData = error instanceof Error ? { message: error.message, stack: error.stack, name: error.name } : error || void 0;
    this.log("error" /* ERROR */, message, { ...metadata, error: errorData });
    if (error instanceof Error && typeof window !== "undefined" && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: { app: { context: this.context } },
        extra: metadata ? sanitizeMetadata(metadata) : void 0
      });
    }
  }
  fatal(message, error, metadata) {
    const errorData = error instanceof Error ? { message: error.message, stack: error.stack } : error || void 0;
    this.log("fatal" /* FATAL */, message, { ...metadata, error: errorData });
    if (typeof window !== "undefined" && window.Sentry) {
      window.Sentry.captureException(error || new Error(message), {
        level: "fatal",
        contexts: {
          app: { context: this.context }
        },
        extra: metadata ? sanitizeMetadata(metadata) : void 0
      });
    }
  }
  log(level, message, metadata) {
    const isServer = typeof window === "undefined";
    const entry = {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      level,
      context: this.context,
      message,
      metadata,
      userId: isServer ? "system" : this.getCurrentUserId(),
      sessionId: isServer ? "system-session" : this.getSessionId()
    };
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    if (isServer) {
      this.serverLog(entry);
      return;
    }
    if (this.isDevelopment()) {
      this.consoleLog(entry);
    }
    if (!this.isDevelopment()) {
      this.sendToBackend(entry);
    }
    if (!this.isDevelopment() && (level === "warn" || level === "error")) {
      this.storeInFirestore(entry);
    }
  }
  /**
   * Server-side logging for Cloud Functions / Node.js.
   * Outputs structured JSON that Cloud Logging picks up with proper severity.
   */
  serverLog(entry) {
    const payload = {
      severity: this.cloudLoggingSeverity(entry.level),
      message: `[${entry.context}] ${entry.message}`,
      context: entry.context
    };
    if (entry.metadata) {
      payload.metadata = entry.metadata;
    }
    const output = JSON.stringify(payload);
    switch (entry.level) {
      case "error" /* ERROR */:
      case "fatal" /* FATAL */:
        console.error(output);
        break;
      case "warn" /* WARN */:
        console.warn(output);
        break;
      default:
        console.log(output);
    }
  }
  cloudLoggingSeverity(level) {
    switch (level) {
      case "debug" /* DEBUG */:
        return "DEBUG";
      case "info" /* INFO */:
        return "INFO";
      case "warn" /* WARN */:
        return "WARNING";
      case "error" /* ERROR */:
        return "ERROR";
      case "fatal" /* FATAL */:
        return "CRITICAL";
      default:
        return "DEFAULT";
    }
  }
  consoleLog(entry) {
    const style = this.getConsoleStyle(entry.level);
    const prefix = `[${entry.level.toUpperCase()}] [${entry.context}]`;
    console.log(
      `%c${prefix}%c ${entry.message}`,
      style,
      "color: inherit",
      entry.metadata || ""
    );
  }
  getConsoleStyle(level) {
    const styles = {
      ["debug" /* DEBUG */]: "color: #6B7280",
      ["info" /* INFO */]: "color: #3B82F6",
      ["warn" /* WARN */]: "color: #F59E0B",
      ["error" /* ERROR */]: "color: #EF4444; font-weight: bold",
      ["fatal" /* FATAL */]: "color: #DC2626; font-weight: bold; font-size: 14px"
    };
    return styles[level];
  }
  async sendToBackend(entry) {
    try {
      if (typeof window === "undefined") {
        return;
      }
      const sanitizedEntry = {
        ...entry,
        metadata: entry.metadata ? sanitizeMetadata(entry.metadata) : void 0
      };
      await fetch("/api/logs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sanitizedEntry)
      });
    } catch (error) {
      console.error("Failed to send log to backend:", error);
    }
  }
  async storeInFirestore(entry) {
    try {
      if (typeof window === "undefined") return;
      const db = window.firebase?.firestore();
      if (!db) return;
      const retention = this.classifyLogRetention(entry);
      const cleanEntry = {
        timestamp: entry.timestamp,
        level: entry.level,
        context: entry.context,
        message: entry.message,
        // Metadatos de retención para limpieza automática
        _retention: retention
      };
      if (entry.metadata !== void 0) {
        cleanEntry.metadata = this.removeUndefined(sanitizeMetadata(entry.metadata));
      }
      if (entry.userId !== void 0) {
        cleanEntry.userId = entry.userId;
      }
      if (entry.sessionId !== void 0) {
        cleanEntry.sessionId = entry.sessionId;
      }
      await db.collection("logs").add(cleanEntry);
    } catch (error) {
      console.error("Failed to store log in Firestore:", error);
    }
  }
  /**
   * Clasifica un log y retorna su política de retención
   */
  classifyLogRetention(entry) {
    const now = /* @__PURE__ */ new Date();
    let policy = "short_term" /* SHORT_TERM */;
    if (PERMANENT_CONTEXTS.includes(entry.context)) {
      policy = "permanent" /* PERMANENT */;
    }
    if (policy !== "permanent" /* PERMANENT */) {
      for (const pattern of PERMANENT_MESSAGE_PATTERNS) {
        if (pattern.test(entry.message)) {
          policy = "permanent" /* PERMANENT */;
          break;
        }
      }
    }
    if (policy !== "permanent" /* PERMANENT */) {
      switch (entry.level) {
        case "error" /* ERROR */:
        case "fatal" /* FATAL */:
          policy = "long_term" /* LONG_TERM */;
          break;
        case "debug" /* DEBUG */:
          policy = "ephemeral" /* EPHEMERAL */;
          break;
        case "info" /* INFO */:
        case "warn" /* WARN */:
        default:
          policy = "short_term" /* SHORT_TERM */;
      }
    }
    let expiresAt = null;
    const expirationDays = {
      ["permanent" /* PERMANENT */]: null,
      ["long_term" /* LONG_TERM */]: 90,
      ["short_term" /* SHORT_TERM */]: 14,
      ["ephemeral" /* EPHEMERAL */]: 7
    };
    const days = expirationDays[policy];
    if (days !== null) {
      const expDate = new Date(now);
      expDate.setDate(expDate.getDate() + days);
      expiresAt = expDate.toISOString();
    }
    return {
      policy,
      expiresAt,
      classifiedAt: now.toISOString()
    };
  }
  // Helper para remover campos undefined de un objeto (recursivo)
  removeUndefined(obj) {
    if (obj === null || obj === void 0) return void 0;
    if (typeof obj !== "object") return obj;
    if (Array.isArray(obj)) return obj.map((item) => this.removeUndefined(item)).filter((item) => item !== void 0);
    const cleaned = {};
    Object.keys(obj).forEach((key) => {
      const value = this.removeUndefined(obj[key]);
      if (value !== void 0) {
        cleaned[key] = value;
      }
    });
    return Object.keys(cleaned).length > 0 ? cleaned : void 0;
  }
  getCurrentUserId() {
    if (typeof window === "undefined") return "system";
    const userId = window.__CURRENT_USER_ID__;
    if (userId) return userId;
    const auth = window.firebase?.auth();
    return auth?.currentUser?.uid || "anonymous";
  }
  getSessionId() {
    if (typeof window === "undefined") return "system-session";
    let sessionId = sessionStorage.getItem("sessionId");
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("sessionId", sessionId);
    }
    return sessionId;
  }
  isDevelopment() {
    if (typeof window === "undefined") {
      return process.env.NODE_ENV !== "production";
    }
    return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  }
  /**
   * Get all logs (for Admin Panel)
   */
  static getAllLogs() {
    return _Logger.instance?.logs || [];
  }
  /**
   * Clear logs
   */
  static clearLogs() {
    if (_Logger.instance) {
      _Logger.instance.logs = [];
    }
  }
};
var logger = Logger.getLogger("App");

// src/utils/date.ts
function formatDate(date, options = {}) {
  const { locale = "es-ES", includeTime = false, relative = false } = options;
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "Fecha inv\xE1lida";
  }
  if (relative) {
    return formatRelativeTime(dateObj, locale);
  }
  const dateOptions = {
    year: "numeric",
    month: "long",
    day: "numeric"
  };
  if (includeTime) {
    dateOptions.hour = "2-digit";
    dateOptions.minute = "2-digit";
  }
  return dateObj.toLocaleDateString(locale, dateOptions);
}
function formatDateShort(date, locale = "es-ES") {
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "-";
  }
  return dateObj.toLocaleDateString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
}
function formatRelativeTime(date, locale = "es-ES") {
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (diffDays > 30) {
    return formatDate(dateObj, { locale });
  } else if (diffDays > 0) {
    return rtf.format(-diffDays, "day");
  } else if (diffHours > 0) {
    return rtf.format(-diffHours, "hour");
  } else if (diffMins > 0) {
    return rtf.format(-diffMins, "minute");
  } else {
    return rtf.format(-diffSecs, "second");
  }
}
function parseDate(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return isNaN(dateStr.getTime()) ? null : dateStr;
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
}
function getTimestamp() {
  return (/* @__PURE__ */ new Date()).toISOString();
}
function formatDateTime(date, locale = "es-ES") {
  const dateObj = parseDate(date);
  if (!dateObj) return "";
  return dateObj.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function isToday(date) {
  const dateObj = parseDate(date);
  if (!dateObj) return false;
  const today = /* @__PURE__ */ new Date();
  return dateObj.getDate() === today.getDate() && dateObj.getMonth() === today.getMonth() && dateObj.getFullYear() === today.getFullYear();
}
function isYesterday(date) {
  const dateObj = parseDate(date);
  if (!dateObj) return false;
  const yesterday = /* @__PURE__ */ new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return dateObj.getDate() === yesterday.getDate() && dateObj.getMonth() === yesterday.getMonth() && dateObj.getFullYear() === yesterday.getFullYear();
}
function isTomorrow(date) {
  const dateObj = parseDate(date);
  if (!dateObj) return false;
  const tomorrow = /* @__PURE__ */ new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return dateObj.getDate() === tomorrow.getDate() && dateObj.getMonth() === tomorrow.getMonth() && dateObj.getFullYear() === tomorrow.getFullYear();
}
function isSameDay(date1, date2) {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  if (!d1 || !d2) return false;
  return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
}
function getDaysDifference(date1, date2) {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  if (!d1 || !d2) return 0;
  const diffMs = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffMs / (1e3 * 60 * 60 * 24));
}
function formatTime(date, locale = "es-ES") {
  const dateObj = parseDate(date);
  if (!dateObj) return "";
  return dateObj.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit"
  });
}
function startOfDay(date = /* @__PURE__ */ new Date()) {
  const d = date instanceof Date ? new Date(date) : new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfDay(date = /* @__PURE__ */ new Date()) {
  const d = date instanceof Date ? new Date(date) : new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

// src/utils/async.ts
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
async function retry(fn, options = {}) {
  const {
    maxRetries = 3,
    initialDelayMs = 1e3,
    maxDelayMs = 3e4,
    exponentialBackoff = true,
    onRetry
  } = options;
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxRetries) {
        const delay = exponentialBackoff ? Math.min(initialDelayMs * Math.pow(2, attempt), maxDelayMs) : initialDelayMs;
        onRetry?.(lastError, attempt + 1);
        await sleep(delay);
      }
    }
  }
  throw lastError;
}
function debounce(fn, delayMs) {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => fn(...args), delayMs);
  };
}
function throttle(fn, limitMs) {
  let lastRun = 0;
  let timeoutId;
  return (...args) => {
    const now = Date.now();
    if (now - lastRun >= limitMs) {
      lastRun = now;
      fn(...args);
    } else {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        lastRun = Date.now();
        fn(...args);
      }, limitMs - (now - lastRun));
    }
  };
}
async function parallelLimit(items, fn, concurrency) {
  const results = [];
  const executing = [];
  let index = 0;
  for (const item of items) {
    const currentIndex = index++;
    const promise = fn(item, currentIndex).then((result) => {
      results[currentIndex] = result;
    });
    executing.push(promise.then(() => {
      executing.splice(executing.indexOf(promise), 1);
    }));
    if (executing.length >= concurrency) {
      await Promise.race(executing);
    }
  }
  await Promise.all(executing);
  return results;
}
async function withTimeout(promise, timeoutMs, errorMessage = "Operation timed out") {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
}

// src/utils/cron.ts
function shouldExecuteCronBefore(cronExpression, timezone = "UTC", date = /* @__PURE__ */ new Date()) {
  const parts = cronExpression.split(" ");
  if (parts.length !== 5) {
    return false;
  }
  const targetDate = new Date(date.toLocaleString("en-US", { timeZone: timezone }));
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  const currentMinute = targetDate.getMinutes();
  const currentHour = targetDate.getHours();
  const currentDayOfMonth = targetDate.getDate();
  const currentMonth = targetDate.getMonth() + 1;
  const currentDayOfWeek = targetDate.getDay();
  if (!matches(minute, currentMinute)) return false;
  if (!matches(hour, currentHour)) return false;
  if (!matches(dayOfMonth, currentDayOfMonth)) return false;
  if (!matches(month, currentMonth)) return false;
  if (dayOfWeek !== "*") {
    const cronDay = parseInt(dayOfWeek);
    const adjustedCurrentDay = currentDayOfWeek;
    if (isNaN(cronDay)) {
      if (!matches(dayOfWeek, adjustedCurrentDay)) return false;
    } else {
      if (cronDay === 7 && adjustedCurrentDay === 0) return true;
      if (!matches(dayOfWeek, adjustedCurrentDay)) return false;
    }
  }
  return true;
}
function shouldExecuteCron(cronExpression, timezone = "UTC") {
  return shouldExecuteCronBefore(cronExpression, timezone, /* @__PURE__ */ new Date());
}
function matches(part, value) {
  if (part === "*") return true;
  if (part.includes(",")) {
    return part.split(",").map((p) => parseInt(p)).includes(value);
  }
  if (part.includes("-")) {
    const [start, end] = part.split("-").map((p) => parseInt(p));
    return value >= start && value <= end;
  }
  if (part.includes("/")) {
    const [base, step] = part.split("/");
    const stepNum = parseInt(step);
    if (base === "*") {
      return value % stepNum === 0;
    }
  }
  return parseInt(part) === value;
}
function validateCronExpression(cronExpression) {
  const parts = cronExpression.split(" ");
  if (parts.length !== 5) return false;
  return true;
}

// src/utils/validation.ts
import { z } from "zod";
function validate(schema, data) {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const messages = error.errors.map((e) => `${e.path.join(".")}: ${e.message}`).join(", ");
      throw new Error(`Validation failed: ${messages}`);
    }
    throw error;
  }
}
var EmailSchema = z.string().email("Invalid email address");
var PasswordSchema = z.string().min(6, "Password must be at least 6 characters");
var UuidSchema = z.string().uuid("Invalid UUID");
var UrlSchema = z.string().url("Invalid URL");
var PhoneSchema = z.string().regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number");
var PaginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).optional().default("desc")
});
var DateRangeSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime()
});

// src/utils/format.ts
function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}
function formatNumber(value, locale = "es-ES", options) {
  return new Intl.NumberFormat(locale, options).format(value);
}
function formatCurrency(value, currency = "EUR", locale = "es-ES") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  }).format(value);
}
function formatPercentage(value, decimals = 2, locale = "es-ES") {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}
function truncate(text, maxLength, suffix = "...") {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - suffix.length).trim() + suffix;
}
var truncateText = truncate;
function capitalize(text) {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
function titleCase(text) {
  return text.toLowerCase().split(" ").map((word) => capitalize(word)).join(" ");
}
function slugify(text) {
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function formatCost(cost) {
  if (cost === null || cost === void 0 || isNaN(cost)) {
    return "$0.00";
  }
  if (cost > 0 && cost < 0.01) {
    return `$${cost.toFixed(4)}`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(cost);
}
function formatCostShort(cost) {
  if (cost === null || cost === void 0 || isNaN(cost)) {
    return "$0";
  }
  if (cost >= 1e3) {
    return `$${(cost / 1e3).toFixed(1)}K`;
  }
  if (cost >= 1) {
    return `$${cost.toFixed(0)}`;
  }
  return `$${cost.toFixed(2)}`;
}
function formatVolume(num, decimals = 1) {
  if (num === null || num === void 0 || isNaN(num)) {
    return "0";
  }
  if (num < 0) {
    return `-${formatVolume(Math.abs(num), decimals)}`;
  }
  if (num >= 1e9) {
    return `${(num / 1e9).toFixed(decimals)}B`;
  }
  if (num >= 1e6) {
    return `${(num / 1e6).toFixed(decimals)}M`;
  }
  if (num >= 1e3) {
    return `${(num / 1e3).toFixed(decimals)}K`;
  }
  return num.toString();
}
function formatTokens(tokens) {
  if (tokens === null || tokens === void 0 || isNaN(tokens)) {
    return "0";
  }
  if (tokens >= 1e6) {
    return `${(tokens / 1e6).toFixed(2)}M`;
  }
  if (tokens >= 1e3) {
    return `${(tokens / 1e3).toFixed(1)}K`;
  }
  return tokens.toString();
}
function formatDuration(seconds) {
  if (seconds === null || seconds === void 0 || isNaN(seconds) || seconds < 0) {
    return "0:00";
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}
function formatMinutes(minutes) {
  if (minutes === null || minutes === void 0 || isNaN(minutes) || minutes < 0) {
    return "0 min";
  }
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}min`;
}
function formatDecimal(num, decimals = 2) {
  if (num === null || num === void 0 || isNaN(num)) {
    return "0";
  }
  return num.toFixed(decimals);
}

// src/utils/phone.ts
function formatPhoneNumber(phone, countryCode = "+34") {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (phone.startsWith("+")) {
    return "+" + cleaned;
  }
  if (cleaned.startsWith("00")) {
    return "+" + cleaned.substring(2);
  }
  const countryDigits = countryCode.replace("+", "");
  if (cleaned.startsWith(countryDigits)) {
    return "+" + cleaned;
  }
  return countryCode + cleaned;
}
function maskPhoneNumber(phone, visibleDigits = 3) {
  if (!phone) return "";
  const cleaned = phone.replace(/\s/g, "");
  if (cleaned.length <= visibleDigits) {
    return cleaned;
  }
  const visiblePart = cleaned.slice(-visibleDigits);
  const maskedLength = cleaned.length - visibleDigits;
  if (cleaned.startsWith("+")) {
    const countryCode = cleaned.substring(0, 3);
    const masked = "X".repeat(Math.min(maskedLength - 3, 6));
    return `${countryCode} ${masked.substring(0, 3)} ${masked.substring(3) || "XXX"} ${visiblePart}`;
  }
  return "X".repeat(maskedLength) + visiblePart;
}
function formatPhoneDisplay(phone) {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("34")) {
    return `+${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5, 8)} ${cleaned.substring(8)}`;
  }
  if (cleaned.length === 9) {
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6)}`;
  }
  return cleaned.replace(/(\d{3})(?=\d)/g, "$1 ");
}
function isValidPhone(phone) {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, "");
  return cleaned.length >= 7 && cleaned.length <= 15;
}
function isSpanishMobile(phone) {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 9 && (cleaned.startsWith("6") || cleaned.startsWith("7"))) {
    return true;
  }
  if (cleaned.length === 11 && cleaned.startsWith("34") && (cleaned.charAt(2) === "6" || cleaned.charAt(2) === "7")) {
    return true;
  }
  return false;
}
function extractCountryCode(phone) {
  if (!phone) return null;
  const match = phone.match(/^\+(\d{1,3})/);
  return match ? `+${match[1]}` : null;
}
function extractNationalNumber(phone) {
  if (!phone) return "";
  let cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("34") && cleaned.length > 9) {
    return cleaned.substring(2);
  }
  return cleaned;
}

// src/utils/text.ts
function truncateWords(text, maxWords, ellipsis = "...") {
  if (!text) return "";
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(" ") + ellipsis;
}
function truncateSmart(text, maxLength, ellipsis = "...") {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  const truncatedLength = maxLength - ellipsis.length;
  if (truncatedLength <= 0) return ellipsis.substring(0, maxLength);
  const lastSpace = text.lastIndexOf(" ", truncatedLength);
  if (lastSpace > 0 && lastSpace > truncatedLength * 0.5) {
    return text.substring(0, lastSpace).trimEnd() + ellipsis;
  }
  return text.substring(0, truncatedLength).trimEnd() + ellipsis;
}
function generateUniqueSlug(baseSlug, existingSlugs) {
  let slug = baseSlug;
  let counter = 1;
  while (existingSlugs.includes(slug)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }
  return slug;
}
function sanitizeForFirestore(data) {
  if (data === null || data === void 0) {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeForFirestore(item)).filter((item) => item !== void 0);
  }
  if (typeof data === "object" && data !== null) {
    const sanitized = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value === void 0) {
        return;
      }
      const sanitizedValue = sanitizeForFirestore(value);
      if (sanitizedValue !== void 0) {
        sanitized[key] = sanitizedValue;
      }
    });
    return sanitized;
  }
  return data;
}
function normalizeForSearch(text) {
  if (!text) return "";
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
}
function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "");
}
function nl2br(text) {
  if (!text) return "";
  return text.replace(/\n/g, "<br>");
}
function getInitials(name, maxInitials = 2) {
  if (!name) return "";
  return name.split(" ").filter((word) => word.length > 0).slice(0, maxInitials).map((word) => word.charAt(0).toUpperCase()).join("");
}
function extractHashtags(text) {
  if (!text) return [];
  const matches2 = text.match(/#[\w\u00C0-\u017F]+/g);
  return matches2 ? matches2.map((tag) => tag.substring(1)) : [];
}
function countWords(text) {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
}
function estimateReadingTime(text, wordsPerMinute = 200) {
  const words = countWords(text);
  const minutes = Math.ceil(words / wordsPerMinute);
  return Math.max(1, minutes);
}

// src/utils/cache.ts
function getCachedData(key, options) {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    if (options?.ttlMs) {
      const age = Date.now() - parsed.timestamp;
      if (age > options.ttlMs) {
        localStorage.removeItem(key);
        return null;
      }
    }
    return parsed.data;
  } catch {
    return null;
  }
}
function setCachedData(key, data) {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    const cached = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cached));
  } catch {
  }
}
function removeCachedData(key) {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch {
  }
}
function hasCachedData(key, options) {
  return getCachedData(key, options) !== null;
}
function getCacheAge(key) {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    return Date.now() - parsed.timestamp;
  } catch {
    return null;
  }
}

// src/types/auth.types.ts
var ROLE_HIERARCHY = {
  "none": 0,
  "viewer": 1,
  "editor": 2,
  "admin": 3,
  "owner": 4
};
function isRoleAtLeast(userRole, requiredRole) {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}
function isPlatformAdmin(user) {
  return user.platformRole === "superadmin" || user.platformRole === "admin";
}

// src/config/apps-catalog.ts
var APPS_CATALOG = {
  "platform-console": {
    name: "Consola de Plataforma",
    description: "Panel central de gesti\xF3n de Boosting Platform",
    icon: "LayoutDashboard",
    color: "#6366f1",
    baseUrl: "https://console.boosting.es",
    modules: {
      "dashboard": {
        name: "Dashboard",
        path: "/",
        icon: "Home",
        description: "Vista general de la plataforma"
      },
      "seo": {
        name: "SEO",
        path: "/seo",
        icon: "Search",
        description: "Asistente SEO con IA"
      },
      "content": {
        name: "Contenido",
        path: "/content",
        icon: "FileText",
        description: "Generaci\xF3n de contenido"
      },
      "projects": {
        name: "Proyectos",
        path: "/projects",
        icon: "FolderKanban",
        description: "Gesti\xF3n de proyectos"
      },
      "integrations": {
        name: "Integraciones",
        path: "/integrations",
        icon: "Plug",
        description: "APIs y servicios externos",
        minRole: "admin"
      },
      "billing": {
        name: "Facturaci\xF3n",
        path: "/billing",
        icon: "CreditCard",
        description: "Consumo y pagos",
        minRole: "admin"
      },
      "settings": {
        name: "Configuraci\xF3n",
        path: "/settings",
        icon: "Settings",
        description: "Configuraci\xF3n de la plataforma",
        minRole: "admin"
      },
      "users": {
        name: "Usuarios",
        path: "/users",
        icon: "Users",
        description: "Gesti\xF3n de usuarios y permisos",
        minRole: "admin"
      }
    }
  },
  "appmor": {
    name: "Appmor",
    description: "Gesti\xF3n de cl\xEDnicas de psicolog\xEDa",
    icon: "Brain",
    color: "#10b981",
    baseUrl: "https://app.appmor.es",
    modules: {
      "dashboard": {
        name: "Panel",
        path: "/",
        icon: "Home"
      },
      "patients": {
        name: "Pacientes",
        path: "/patients",
        icon: "Users",
        description: "Gesti\xF3n de pacientes"
      },
      "calendar": {
        name: "Agenda",
        path: "/calendar",
        icon: "Calendar",
        description: "Calendario de citas"
      },
      "sessions": {
        name: "Sesiones",
        path: "/sessions",
        icon: "MessageCircle",
        description: "Registro de sesiones"
      },
      "content": {
        name: "Contenido",
        path: "/content",
        icon: "FileText",
        description: "Blog y recursos"
      },
      "billing": {
        name: "Facturaci\xF3n",
        path: "/billing",
        icon: "Receipt",
        minRole: "admin"
      },
      "settings": {
        name: "Configuraci\xF3n",
        path: "/settings",
        icon: "Settings",
        minRole: "admin"
      }
    }
  },
  "eva": {
    name: "EVA",
    description: "Gesti\xF3n de ventas B2B",
    icon: "ShoppingCart",
    color: "#f59e0b",
    baseUrl: "https://eva.boosting.es",
    modules: {
      "dashboard": {
        name: "Panel",
        path: "/",
        icon: "Home"
      },
      "clients": {
        name: "Clientes",
        path: "/clients",
        icon: "Building2"
      },
      "orders": {
        name: "Pedidos",
        path: "/orders",
        icon: "Package"
      },
      "products": {
        name: "Productos",
        path: "/products",
        icon: "Box"
      },
      "catalog": {
        name: "Cat\xE1logo",
        path: "/catalog",
        icon: "BookOpen"
      },
      "reports": {
        name: "Informes",
        path: "/reports",
        icon: "BarChart3",
        minRole: "editor"
      },
      "settings": {
        name: "Configuraci\xF3n",
        path: "/settings",
        icon: "Settings",
        minRole: "admin"
      }
    }
  },
  "financehub": {
    name: "FinanceHub",
    description: "Gesti\xF3n financiera para gestor\xEDas",
    icon: "Landmark",
    color: "#3b82f6",
    baseUrl: "https://financehub.boosting.es",
    modules: {
      "dashboard": {
        name: "Panel",
        path: "/",
        icon: "Home"
      },
      "clients": {
        name: "Clientes",
        path: "/clients",
        icon: "Users"
      },
      "documents": {
        name: "Documentos",
        path: "/documents",
        icon: "FileStack"
      },
      "accounting": {
        name: "Contabilidad",
        path: "/accounting",
        icon: "Calculator"
      },
      "tax": {
        name: "Impuestos",
        path: "/tax",
        icon: "Receipt"
      },
      "reports": {
        name: "Informes",
        path: "/reports",
        icon: "BarChart3",
        minRole: "editor"
      },
      "settings": {
        name: "Configuraci\xF3n",
        path: "/settings",
        icon: "Settings",
        minRole: "admin"
      }
    }
  }
};
var APP_IDS = Object.keys(APPS_CATALOG);
function getAppDefinition(appId) {
  return APPS_CATALOG[appId];
}
function getModuleDefinition(appId, moduleId) {
  const app = APPS_CATALOG[appId];
  if (!app) return void 0;
  return app.modules[moduleId];
}
function getAppModules(appId) {
  const app = APPS_CATALOG[appId];
  if (!app) return [];
  return Object.entries(app.modules).map(([id, definition]) => ({
    id,
    definition
  }));
}
function isValidAppModule(appId, moduleId) {
  const app = APPS_CATALOG[appId];
  if (!app) return false;
  if (!moduleId) return true;
  return moduleId in app.modules;
}

// src/utils/errors.ts
import { ZodError } from "zod";
var logger2 = Logger.getLogger("ErrorHandler");
var AppError = class extends Error {
  constructor(code, message, statusCode = 500, details) {
    super(message);
    this.code = code;
    this.message = message;
    this.statusCode = statusCode;
    this.details = details;
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, new.target.prototype);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
};
var ValidationError = class extends AppError {
  constructor(message, details) {
    super("VALIDATION_ERROR", message, 400, details);
  }
};
var BusinessError = class extends AppError {
  constructor(message, details) {
    super("BUSINESS_ERROR", message, 422, details);
  }
};
var NotFoundError = class extends AppError {
  constructor(resource, id) {
    const message = id ? `${resource} with id ${id} not found` : `${resource} not found`;
    super("NOT_FOUND", message, 404);
  }
};
var UnauthorizedError = class extends AppError {
  constructor(message = "No autorizado") {
    super("UNAUTHORIZED", message, 401);
  }
};
var ForbiddenError = class extends AppError {
  constructor(message = "No tienes permisos para esta acci\xF3n") {
    super("FORBIDDEN", message, 403);
  }
};
var ConflictError = class extends AppError {
  constructor(message) {
    super("CONFLICT", message, 409);
  }
};
var RateLimitError = class extends AppError {
  constructor(message = "Demasiadas solicitudes. Int\xE9ntalo m\xE1s tarde") {
    super("RATE_LIMIT", message, 429);
  }
};
var ErrorHandler = class {
  static handle(error) {
    if (error instanceof ZodError) {
      const firstError = error.errors?.[0];
      if (firstError) {
        return firstError.message || "Error de validaci\xF3n";
      }
      return "Error de validaci\xF3n";
    }
    if (error instanceof AppError) {
      return error.message;
    }
    if (typeof error === "object" && error !== null && "code" in error) {
      const firebaseError = error;
      if (firebaseError.code?.startsWith("auth/")) {
        return this.handleFirebaseAuthError(firebaseError);
      }
      if (firebaseError.code?.startsWith("permission-denied")) {
        return "No tienes permisos para realizar esta acci\xF3n";
      }
    }
    logger2.error("Unexpected error", error);
    if (typeof window !== "undefined" && window.Sentry) {
      window.Sentry.captureException(error);
    }
    return "Ocurri\xF3 un error inesperado. Por favor, int\xE9ntalo de nuevo.";
  }
  static handleFirebaseAuthError(error) {
    const errorMessages = {
      "auth/email-already-in-use": "Este email ya est\xE1 registrado",
      "auth/weak-password": "La contrase\xF1a es muy d\xE9bil. M\xEDnimo 6 caracteres",
      "auth/invalid-email": "El email no es v\xE1lido",
      "auth/user-not-found": "Usuario no encontrado",
      "auth/wrong-password": "Contrase\xF1a incorrecta",
      "auth/too-many-requests": "Demasiados intentos. Int\xE9ntalo m\xE1s tarde",
      "auth/network-request-failed": "Error de conexi\xF3n. Verifica tu internet",
      "auth/invalid-login-credentials": "Email o contrase\xF1a incorrectos"
    };
    return errorMessages[error.code] || "Error de autenticaci\xF3n";
  }
};
function assert(condition, message) {
  if (!condition) {
    throw new BusinessError(message);
  }
}
function assertExists(value, resource) {
  if (value === null || value === void 0) {
    throw new NotFoundError(resource);
  }
}

// src/security/encryption.service.ts
var logger3 = Logger.getLogger("EncryptionService");
var EncryptionService = class {
  algorithm = "AES-GCM";
  keyLength = 256;
  /**
   * Genera una clave derivada única por usuario usando PBKDF2
   * @param masterKey - Clave maestra (KEK) desde Secret Manager
   * @param userId - ID del usuario
   * @returns CryptoKey para encriptación
   */
  async deriveKey(masterKey, userId) {
    try {
      const encoder = new TextEncoder();
      const keyMaterial = await crypto.subtle.importKey(
        "raw",
        encoder.encode(masterKey),
        "PBKDF2",
        false,
        ["deriveBits", "deriveKey"]
      );
      const derivedKey = await crypto.subtle.deriveKey(
        {
          name: "PBKDF2",
          salt: encoder.encode(userId),
          iterations: 1e5,
          hash: "SHA-256"
        },
        keyMaterial,
        {
          name: this.algorithm,
          length: this.keyLength
        },
        true,
        ["encrypt", "decrypt"]
      );
      return derivedKey;
    } catch (error) {
      logger3.error("Failed to derive key", error);
      throw new Error("Error derivando clave de encriptaci\xF3n");
    }
  }
  /**
   * Encripta datos sensibles usando AES-256-GCM
   * @param plaintext - Texto a encriptar
   * @param userId - ID del usuario (para derivar clave única)
   * @param masterKey - Clave maestra desde Secret Manager
   * @returns EncryptedField
   */
  async encrypt(plaintext, userId, masterKey) {
    try {
      logger3.info("Encrypting data", { userId, dataLength: plaintext.length });
      const key = await this.deriveKey(masterKey, userId);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encoder = new TextEncoder();
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv
        },
        key,
        encoder.encode(plaintext)
      );
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const ciphertext = encryptedArray.slice(0, -16);
      const authTag = encryptedArray.slice(-16);
      const encryptedHex = this.arrayBufferToHex(ciphertext);
      const ivHex = this.arrayBufferToHex(iv);
      const authTagHex = this.arrayBufferToHex(authTag);
      logger3.info("Data encrypted successfully", { userId });
      return {
        encryptedData: encryptedHex,
        iv: ivHex,
        authTag: authTagHex,
        encryptedBy: userId,
        encryptedAt: (/* @__PURE__ */ new Date()).toISOString(),
        keyVersion: "1.0.0"
      };
    } catch (error) {
      logger3.error("Encryption failed", error);
      throw new Error("Error encriptando datos sensibles");
    }
  }
  /**
   * Desencripta datos
   * @param encryptedField - Campo encriptado
   * @param userId - ID del usuario
   * @param masterKey - Clave maestra
   * @returns Texto desencriptado
   */
  async decrypt(encryptedField, userId, masterKey) {
    try {
      logger3.info("Decrypting data", { userId });
      const key = await this.deriveKey(masterKey, userId);
      const ciphertext = this.hexToArrayBuffer(encryptedField.encryptedData);
      const iv = this.hexToArrayBuffer(encryptedField.iv);
      const authTag = this.hexToArrayBuffer(encryptedField.authTag);
      const combined = new Uint8Array(ciphertext.byteLength + authTag.byteLength);
      combined.set(new Uint8Array(ciphertext), 0);
      combined.set(new Uint8Array(authTag), ciphertext.byteLength);
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv
        },
        key,
        combined
      );
      const decoder = new TextDecoder();
      const plaintext = decoder.decode(decryptedBuffer);
      logger3.info("Data decrypted successfully", { userId });
      return plaintext;
    } catch (error) {
      logger3.error("Decryption failed", error);
      throw new Error("Error desencriptando datos. Puede que la clave haya cambiado.");
    }
  }
  /**
   * Verifica si un campo está encriptado correctamente
   */
  isValidEncryptedField(field) {
    if (typeof field !== "object" || field === null) return false;
    const f = field;
    return typeof f.encryptedData === "string" && typeof f.iv === "string" && typeof f.authTag === "string" && typeof f.encryptedBy === "string" && typeof f.encryptedAt === "string";
  }
  // ============================================
  // HELPERS
  // ============================================
  arrayBufferToHex(buffer) {
    const array = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    return Array.from(array).map((byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  hexToArrayBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
  }
  /**
   * Genera hash SHA-256 de un string (para comparaciones sin revelar datos)
   */
  async hash(data) {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
    return this.arrayBufferToHex(hashBuffer);
  }
};
function createEncryptionService() {
  return new EncryptionService();
}

// src/security/audit-log.service.ts
var logger4 = Logger.getLogger("AuditLogService");
var AuditLogService = class {
  db;
  collection;
  constructor(db, collectionName = "audit_logs") {
    this.db = db;
    this.collection = collectionName;
  }
  /**
   * Registrar una entrada de auditoría
   */
  async log(data) {
    try {
      const entry = {
        userId: data.userId,
        userEmail: data.userEmail,
        userRole: data.userRole || "unknown",
        action: data.action,
        resourceType: data.resourceType,
        resourceId: data.resourceId || "",
        details: data.details,
        timestamp: (/* @__PURE__ */ new Date()).toISOString(),
        success: data.success ?? true,
        errorMessage: data.errorMessage,
        // Browser context (si está disponible)
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : void 0
      };
      await this.db.collection(this.collection).add(entry);
      logger4.info("Audit log created", {
        action: data.action,
        resourceType: data.resourceType,
        resourceId: data.resourceId,
        userId: data.userId
      });
    } catch (error) {
      logger4.error("Failed to create audit log", error);
    }
  }
  /**
   * Registrar visualización de datos sensibles
   */
  async logView(userId, userRole, resourceType, resourceId, details) {
    await this.log({
      userId,
      userRole,
      action: "view",
      resourceType,
      resourceId,
      details
    });
  }
  /**
   * Registrar modificación de datos
   */
  async logUpdate(userId, userRole, resourceType, resourceId, changes) {
    await this.log({
      userId,
      userRole,
      action: "update",
      resourceType,
      resourceId,
      details: { changes }
    });
  }
  /**
   * Registrar acceso denegado
   */
  async logAccessDenied(userId, userRole, resourceType, resourceId, reason) {
    await this.log({
      userId,
      userRole,
      action: "access_denied",
      resourceType,
      resourceId,
      success: false,
      errorMessage: reason
    });
  }
  /**
   * Obtener logs por usuario
   */
  async getByUser(userId, limit = 100) {
    try {
      const snapshot = await this.db.collection(this.collection).where("userId", "==", userId).orderBy("timestamp", "desc").limit(limit).get();
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger4.error("Failed to get audit logs by user", error);
      throw error;
    }
  }
  /**
   * Obtener logs por recurso
   */
  async getByResource(resourceType, resourceId, limit = 50) {
    try {
      const snapshot = await this.db.collection(this.collection).where("resourceType", "==", resourceType).where("resourceId", "==", resourceId).orderBy("timestamp", "desc").limit(limit).get();
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger4.error("Failed to get audit logs by resource", error);
      throw error;
    }
  }
  /**
   * Obtener logs de accesos denegados (seguridad)
   */
  async getAccessDeniedLogs(limit = 100) {
    try {
      const snapshot = await this.db.collection(this.collection).where("action", "==", "access_denied").orderBy("timestamp", "desc").limit(limit).get();
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger4.error("Failed to get access denied logs", error);
      throw error;
    }
  }
};
function createAuditLogService(db, collectionName) {
  return new AuditLogService(db, collectionName);
}

// src/security/password-policy.service.ts
var logger5 = Logger.getLogger("PasswordPolicy");
var DEFAULT_PASSWORD_POLICY = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
  historySize: 5,
  maxAgeDays: null
  // Sin expiración por defecto (NIST recomienda no forzar cambios)
};
var PasswordPolicyService = class {
  config;
  constructor(config) {
    this.config = { ...DEFAULT_PASSWORD_POLICY, ...config };
  }
  /**
   * Validar complejidad de contraseña
   */
  validateComplexity(password) {
    const errors = [];
    if (password.length < this.config.minLength) {
      errors.push(`Debe tener al menos ${this.config.minLength} caracteres`);
    }
    if (this.config.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push("Debe incluir al menos una may\xFAscula");
    }
    if (this.config.requireLowercase && !/[a-z]/.test(password)) {
      errors.push("Debe incluir al menos una min\xFAscula");
    }
    if (this.config.requireNumber && !/\d/.test(password)) {
      errors.push("Debe incluir al menos un n\xFAmero");
    }
    if (this.config.requireSpecialChar && !/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\/~`]/.test(password)) {
      errors.push("Debe incluir al menos un car\xE1cter especial (!@#$%^&*...)");
    }
    const weakPatterns = [
      /^(.)\1+$/,
      // Todos iguales: "aaaaaaa"
      /^12345/,
      // Secuencias numéricas
      /^qwerty/i,
      // Patrones de teclado
      /password/i,
      // Palabra "password"
      /contraseña/i
      // Palabra "contraseña"
    ];
    for (const pattern of weakPatterns) {
      if (pattern.test(password)) {
        errors.push("La contrase\xF1a contiene patrones d\xE9biles");
        break;
      }
    }
    return {
      valid: errors.length === 0,
      errors
    };
  }
  /**
   * Hashear contraseña para almacenar en historial
   * Usamos SHA-256 ya que solo es para comparación, no autenticación
   */
  async hashPassword(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  /**
   * Comparar contraseña con hash almacenado
   */
  async compareWithHash(password, storedHash) {
    const passwordHash = await this.hashPassword(password);
    return passwordHash === storedHash;
  }
  /**
   * Obtener configuración actual
   */
  getConfig() {
    return { ...this.config };
  }
  /**
   * Actualizar configuración
   */
  setConfig(config) {
    this.config = { ...this.config, ...config };
    logger5.info("Password policy config updated", { config: this.config });
  }
  /**
   * Obtener requisitos en formato legible para UI
   */
  getRequirementsText() {
    const requirements = [];
    requirements.push(`M\xEDnimo ${this.config.minLength} caracteres`);
    if (this.config.requireUppercase) {
      requirements.push("Al menos una may\xFAscula (A-Z)");
    }
    if (this.config.requireLowercase) {
      requirements.push("Al menos una min\xFAscula (a-z)");
    }
    if (this.config.requireNumber) {
      requirements.push("Al menos un n\xFAmero (0-9)");
    }
    if (this.config.requireSpecialChar) {
      requirements.push("Al menos un car\xE1cter especial (!@#$%...)");
    }
    if (this.config.historySize > 0) {
      requirements.push(`No reutilizar las \xFAltimas ${this.config.historySize} contrase\xF1as`);
    }
    return requirements;
  }
  /**
   * Verificar si una fecha de cambio de contraseña ha expirado
   */
  isExpired(passwordChangedAt) {
    if (!this.config.maxAgeDays || !passwordChangedAt) {
      return false;
    }
    const changedAt = passwordChangedAt instanceof Date ? passwordChangedAt : new Date(passwordChangedAt);
    const expiresAt = new Date(changedAt.getTime() + this.config.maxAgeDays * 24 * 60 * 60 * 1e3);
    return /* @__PURE__ */ new Date() > expiresAt;
  }
};
function createPasswordPolicyService(config) {
  return new PasswordPolicyService(config);
}

// src/security/session.service.ts
var logger6 = Logger.getLogger("SessionService");
var DEFAULT_SESSION_CONFIG = {
  maxSessions: 5,
  collectionName: "active_sessions",
  sessionStorageKey: "boosting_session_id",
  activityIntervalMs: 5 * 60 * 1e3
  // 5 minutes
};
var SessionService = class {
  db;
  config;
  activityInterval = null;
  constructor(db, config) {
    this.db = db;
    this.config = { ...DEFAULT_SESSION_CONFIG, ...config };
  }
  /**
   * Registrar nueva sesión al hacer login
   */
  async registerSession(userId) {
    try {
      const deviceInfo = this.getDeviceInfo();
      const activeSessions = await this.getActiveSessions(userId);
      if (activeSessions.length >= this.config.maxSessions) {
        const oldest = activeSessions.sort(
          (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime()
        )[0];
        await this.revokeSession(oldest.id);
        logger6.warn("Session limit reached, closed oldest", {
          userId,
          closedSessionId: oldest.id
        });
      }
      const session = {
        userId,
        deviceInfo,
        startedAt: (/* @__PURE__ */ new Date()).toISOString(),
        lastActivity: (/* @__PURE__ */ new Date()).toISOString()
      };
      const docRef = await this.db.collection(this.config.collectionName).add(session);
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(this.config.sessionStorageKey, docRef.id);
      }
      this.startActivityTracking();
      logger6.info("Session registered", { userId, sessionId: docRef.id });
      return docRef.id;
    } catch (error) {
      logger6.error("Failed to register session", error);
      throw error;
    }
  }
  /**
   * Obtener sesiones activas del usuario
   */
  async getActiveSessions(userId) {
    try {
      const currentSessionId = typeof localStorage !== "undefined" ? localStorage.getItem(this.config.sessionStorageKey) : null;
      const snapshot = await this.db.collection(this.config.collectionName).where("userId", "==", userId).orderBy("lastActivity", "desc").get();
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        current: doc.id === currentSessionId
      }));
    } catch (error) {
      logger6.error("Failed to get active sessions", error);
      return [];
    }
  }
  /**
   * Cerrar una sesión específica
   */
  async revokeSession(sessionId) {
    try {
      await this.db.collection(this.config.collectionName).doc(sessionId).delete();
      logger6.info("Session revoked", { sessionId });
    } catch (error) {
      logger6.error("Failed to revoke session", error);
      throw error;
    }
  }
  /**
   * Cerrar TODAS las sesiones (logout global)
   */
  async revokeAllSessions(userId) {
    try {
      const sessions = await this.getActiveSessions(userId);
      const batch = this.db.batch();
      sessions.forEach((session) => {
        batch.delete(this.db.collection(this.config.collectionName).doc(session.id));
      });
      await batch.commit();
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem(this.config.sessionStorageKey);
      }
      this.stopActivityTracking();
      logger6.info("All sessions revoked", { userId, count: sessions.length });
      return sessions.length;
    } catch (error) {
      logger6.error("Failed to revoke all sessions", error);
      throw error;
    }
  }
  /**
   * Actualizar última actividad
   */
  async touchSession() {
    const sessionId = typeof localStorage !== "undefined" ? localStorage.getItem(this.config.sessionStorageKey) : null;
    if (!sessionId) return;
    try {
      await this.db.collection(this.config.collectionName).doc(sessionId).update({
        lastActivity: (/* @__PURE__ */ new Date()).toISOString()
      });
    } catch (error) {
      logger6.debug("Touch session failed", { sessionId });
    }
  }
  /**
   * Cerrar sesión actual (al hacer logout)
   */
  async endCurrentSession() {
    const sessionId = typeof localStorage !== "undefined" ? localStorage.getItem(this.config.sessionStorageKey) : null;
    if (sessionId) {
      await this.revokeSession(sessionId);
    }
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(this.config.sessionStorageKey);
    }
    this.stopActivityTracking();
  }
  /**
   * Iniciar tracking de actividad
   */
  startActivityTracking() {
    this.activityInterval = setInterval(() => {
      this.touchSession();
    }, this.config.activityIntervalMs);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
  }
  /**
   * Detener tracking
   */
  stopActivityTracking() {
    if (this.activityInterval) {
      clearInterval(this.activityInterval);
      this.activityInterval = null;
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
  }
  handleVisibilityChange = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      this.touchSession();
    }
  };
  /**
   * Obtener info del dispositivo actual
   */
  getDeviceInfo() {
    if (typeof navigator === "undefined") {
      return { browser: "Unknown", os: "Unknown", device: "Desktop" };
    }
    const ua = navigator.userAgent;
    let browser = "Unknown";
    if (ua.includes("Chrome") && !ua.includes("Edge")) browser = "Chrome";
    else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
    else if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Edge")) browser = "Edge";
    let os = "Unknown";
    if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac")) os = "macOS";
    else if (ua.includes("iPhone")) os = "iOS";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("Linux")) os = "Linux";
    let device = "Desktop";
    if (ua.includes("iPhone") || ua.includes("Android") && ua.includes("Mobile")) {
      device = "Mobile";
    } else if (ua.includes("iPad") || ua.includes("Android") && !ua.includes("Mobile")) {
      device = "Tablet";
    }
    return { browser, os, device };
  }
  /**
   * Verificar si hay una sesión activa válida
   */
  async isSessionValid() {
    const sessionId = typeof localStorage !== "undefined" ? localStorage.getItem(this.config.sessionStorageKey) : null;
    if (!sessionId) return false;
    try {
      const doc = await this.db.collection(this.config.collectionName).doc(sessionId).get();
      return doc.exists;
    } catch {
      return false;
    }
  }
};
function createSessionService(db, config) {
  return new SessionService(db, config);
}

// src/security/access-control.service.ts
var logger7 = Logger.getLogger("AccessControlService");
var DEFAULT_ACCESS_CONFIG = {
  usersCollection: "users"
};
var DEFAULT_access_CONFIG = DEFAULT_ACCESS_CONFIG;
var AccessControlService = class {
  db;
  config;
  constructor(db, config) {
    this.db = db;
    this.config = { ...DEFAULT_ACCESS_CONFIG, ...config };
  }
  // ============================================
  // USER RETRIEVAL
  // ============================================
  /**
   * Obtiene el perfil completo de un usuario
   */
  async getUser(uid) {
    try {
      const doc = await this.db.collection(this.config.usersCollection).doc(uid).get();
      if (!doc.exists) return null;
      return doc.data();
    } catch (error) {
      logger7.error("Failed to get user", error);
      throw error;
    }
  }
  // ============================================
  // ACCESS CHECKS - APP LEVEL
  // ============================================
  /**
   * Verifica si un usuario tiene acceso básico a una aplicación.
   * Si es Admin global, siempre retorna true.
   */
  async canAccessApp(uid, appId) {
    const user = await this.getUser(uid);
    if (!user) return false;
    return this.checkAppAccess(user, appId).allowed;
  }
  /**
   * Verifica acceso a app de forma síncrona (cuando ya tienes el usuario)
   */
  checkAppAccess(user, appId) {
    if (user.status !== "active") {
      return { allowed: false, reason: "User is not active" };
    }
    if (isPlatformAdmin(user)) {
      return { allowed: true, reason: "Platform admin", effectiveRole: "owner" };
    }
    const appAccess = user.apps?.[appId];
    if (!appAccess) {
      return { allowed: false, reason: "No app access configured" };
    }
    if (!appAccess.enabled) {
      return { allowed: false, reason: "App access disabled" };
    }
    if (appAccess.role === "none") {
      return { allowed: false, reason: "Role is none" };
    }
    return {
      allowed: true,
      effectiveRole: appAccess.role
    };
  }
  // ============================================
  // ACCESS CHECKS - MODULE LEVEL
  // ============================================
  /**
   * Verifica si un usuario tiene acceso a un módulo específico de una app.
   * Si el módulo no está definido, hereda el rol de la app.
   */
  async canAccessModule(uid, appId, moduleId) {
    const user = await this.getUser(uid);
    if (!user) return false;
    return this.checkModuleAccess(user, appId, moduleId).allowed;
  }
  /**
   * Verifica acceso a módulo de forma síncrona
   */
  checkModuleAccess(user, appId, moduleId) {
    const appCheck = this.checkAppAccess(user, appId);
    if (!appCheck.allowed) {
      return appCheck;
    }
    if (isPlatformAdmin(user)) {
      return { allowed: true, reason: "Platform admin", effectiveRole: "owner" };
    }
    const appAccess = user.apps[appId];
    const moduleAccess = appAccess.modules?.[moduleId];
    if (!moduleAccess) {
      return {
        allowed: true,
        reason: "Inherited from app role",
        effectiveRole: appAccess.role
      };
    }
    if (!moduleAccess.enabled) {
      return { allowed: false, reason: "Module access disabled" };
    }
    if (moduleAccess.role === "none") {
      return { allowed: false, reason: "Module role is none" };
    }
    return {
      allowed: true,
      effectiveRole: moduleAccess.role
    };
  }
  // ============================================
  // ROLE CHECKS
  // ============================================
  /**
   * Verifica si un usuario tiene al menos un rol específico en una app
   */
  async checkPermission(uid, appId, requiredRole) {
    const user = await this.getUser(uid);
    if (!user) return false;
    return this.checkRolePermission(user, appId, requiredRole).allowed;
  }
  /**
   * Verifica rol de forma síncrona
   */
  checkRolePermission(user, appId, requiredRole) {
    const appCheck = this.checkAppAccess(user, appId);
    if (!appCheck.allowed) {
      return appCheck;
    }
    if (isPlatformAdmin(user)) {
      return { allowed: true, effectiveRole: "owner" };
    }
    const effectiveRole = appCheck.effectiveRole;
    const hasPermission = isRoleAtLeast(effectiveRole, requiredRole);
    return {
      allowed: hasPermission,
      reason: hasPermission ? void 0 : `Required role: ${requiredRole}, has: ${effectiveRole}`,
      effectiveRole
    };
  }
  /**
   * Verifica si un usuario tiene al menos un rol específico en un módulo
   */
  async checkModulePermission(uid, appId, moduleId, requiredRole) {
    const user = await this.getUser(uid);
    if (!user) return false;
    return this.checkModuleRolePermission(user, appId, moduleId, requiredRole).allowed;
  }
  /**
   * Verifica rol en módulo de forma síncrona
   */
  checkModuleRolePermission(user, appId, moduleId, requiredRole) {
    const moduleCheck = this.checkModuleAccess(user, appId, moduleId);
    if (!moduleCheck.allowed) {
      return moduleCheck;
    }
    const effectiveRole = moduleCheck.effectiveRole;
    const hasPermission = isRoleAtLeast(effectiveRole, requiredRole);
    return {
      allowed: hasPermission,
      reason: hasPermission ? void 0 : `Required role: ${requiredRole}, has: ${effectiveRole}`,
      effectiveRole
    };
  }
  // ============================================
  // GRANT / REVOKE ACCESS
  // ============================================
  /**
   * Otorga o actualiza acceso a una aplicación
   */
  async grantAppAccess(targetUid, appId, access, adminUid) {
    const updateData = {
      [`apps.${appId}`]: {
        ...access,
        grantedBy: adminUid,
        grantedAt: (/* @__PURE__ */ new Date()).toISOString(),
        enabled: access.enabled ?? true
      }
    };
    await this.db.collection(this.config.usersCollection).doc(targetUid).update(updateData);
    logger7.info("App access granted", { targetUid, appId, role: access.role, grantedBy: adminUid });
  }
  /**
   * Otorga o actualiza acceso a un módulo específico
   */
  async grantModuleAccess(targetUid, appId, moduleId, moduleAccess, adminUid) {
    const updateData = {
      [`apps.${appId}.modules.${moduleId}`]: moduleAccess
    };
    await this.db.collection(this.config.usersCollection).doc(targetUid).update(updateData);
    logger7.info("Module access granted", {
      targetUid,
      appId,
      moduleId,
      role: moduleAccess.role,
      grantedBy: adminUid
    });
  }
  /**
   * Revoca el acceso a una aplicación
   */
  async revokeAppAccess(targetUid, appId) {
    const updateData = {
      [`apps.${appId}.enabled`]: false,
      [`apps.${appId}.role`]: "none"
    };
    await this.db.collection(this.config.usersCollection).doc(targetUid).update(updateData);
    logger7.info("App access revoked", { targetUid, appId });
  }
  /**
   * Revoca el acceso a un módulo específico
   */
  async revokeModuleAccess(targetUid, appId, moduleId) {
    const updateData = {
      [`apps.${appId}.modules.${moduleId}`]: {
        enabled: false,
        role: "none"
      }
    };
    await this.db.collection(this.config.usersCollection).doc(targetUid).update(updateData);
    logger7.info("Module access revoked", { targetUid, appId, moduleId });
  }
  // ============================================
  // UTILITY METHODS
  // ============================================
  /**
   * Obtiene el rol efectivo de un usuario en una app
   */
  getEffectiveAppRole(user, appId) {
    if (isPlatformAdmin(user)) return "owner";
    const appAccess = user.apps?.[appId];
    if (!appAccess || !appAccess.enabled) return "none";
    return appAccess.role;
  }
  /**
   * Obtiene el rol efectivo de un usuario en un módulo
   */
  getEffectiveModuleRole(user, appId, moduleId) {
    if (isPlatformAdmin(user)) return "owner";
    const appAccess = user.apps?.[appId];
    if (!appAccess || !appAccess.enabled) return "none";
    const moduleAccess = appAccess.modules?.[moduleId];
    if (!moduleAccess) return appAccess.role;
    if (!moduleAccess.enabled) return "none";
    return moduleAccess.role;
  }
  /**
   * Lista todos los módulos accesibles para un usuario en una app
   */
  getAccessibleModules(user, appId, allModules) {
    const appCheck = this.checkAppAccess(user, appId);
    if (!appCheck.allowed) return [];
    if (isPlatformAdmin(user)) return allModules;
    const appAccess = user.apps[appId];
    return allModules.filter((moduleId) => {
      const moduleAccess = appAccess.modules?.[moduleId];
      if (!moduleAccess) return true;
      return moduleAccess.enabled && moduleAccess.role !== "none";
    });
  }
};
function createAccessControlService(db, config) {
  return new AccessControlService(db, config);
}
export {
  APPS_CATALOG,
  APP_IDS,
  AccessControlService,
  AppError,
  AuditLogService,
  BusinessError,
  ConflictError,
  DEFAULT_ACCESS_CONFIG,
  DEFAULT_PASSWORD_POLICY,
  DEFAULT_SESSION_CONFIG,
  DEFAULT_access_CONFIG,
  DateRangeSchema,
  EmailSchema,
  EncryptionService,
  ErrorHandler,
  ForbiddenError,
  LogLevel,
  LogRetentionPolicy,
  Logger,
  NotFoundError,
  PaginationSchema,
  PasswordPolicyService,
  PasswordSchema,
  PhoneSchema,
  ROLE_HIERARCHY,
  RateLimitError,
  SessionService,
  UnauthorizedError,
  UrlSchema,
  UuidSchema,
  ValidationError,
  assert,
  assertExists,
  capitalize,
  countWords,
  createAccessControlService,
  createAuditLogService,
  createEncryptionService,
  createPasswordPolicyService,
  createSessionService,
  debounce,
  endOfDay,
  estimateReadingTime,
  extractCountryCode,
  extractHashtags,
  extractNationalNumber,
  formatBytes,
  formatCost,
  formatCostShort,
  formatCurrency,
  formatDate,
  formatDateShort,
  formatDateTime,
  formatDecimal,
  formatDuration,
  formatMinutes,
  formatNumber,
  formatPercentage,
  formatPhoneDisplay,
  formatPhoneNumber,
  formatRelativeTime,
  formatTime,
  formatTokens,
  formatVolume,
  generateUniqueSlug,
  getAppDefinition,
  getAppModules,
  getCacheAge,
  getCachedData,
  getDaysDifference,
  getInitials,
  getModuleDefinition,
  getTimestamp,
  hasCachedData,
  isPlatformAdmin,
  isRoleAtLeast,
  isSameDay,
  isSpanishMobile,
  isToday,
  isTomorrow,
  isValidAppModule,
  isValidPhone,
  isYesterday,
  logger,
  maskPhoneNumber,
  nl2br,
  normalizeForSearch,
  parallelLimit,
  parseDate,
  removeCachedData,
  retry,
  sanitizeForFirestore,
  setCachedData,
  shouldExecuteCron,
  shouldExecuteCronBefore,
  sleep,
  slugify,
  startOfDay,
  stripHtml,
  throttle,
  titleCase,
  truncateSmart,
  truncateText,
  truncateWords,
  validate,
  validateCronExpression,
  withTimeout,
  z
};
